import { ApplicationConfig, ErrorHandler, importProvidersFrom, isDevMode, LOCALE_ID, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { AuthConfig, OAuthModule, OAuthModuleConfig, OAuthStorage } from 'angular-oauth2-oidc';
import { InputMaskModule } from '@ngneat/input-mask';
import { GoogleMapsModule } from '@angular/google-maps';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AppState } from './app.state';
import { CustomIconService } from './core/services/custom-icon.service';
import { DatadogService } from './shared/services/datadog.service';
import { LocalStorageDataService } from './shared/services/local-storage.service';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { getPaginatorIntl } from './shared/logic/paginator-intl';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarConfig } from '@angular/material/snack-bar';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { ErrorStateMatcher, MAT_RIPPLE_GLOBAL_OPTIONS, MatDateFormats, RippleGlobalOptions } from '@angular/material/core';
import { MAT_SELECT_CONFIG, MatSelectConfig } from '@angular/material/select';
import { MAT_AUTOCOMPLETE_DEFAULT_OPTIONS, MatAutocompleteDefaultOptions } from '@angular/material/autocomplete';
import { InstantErrorStateMatcher } from './shared/state-matchers/instant-error-state';
import { EnvironmentLoaderService } from './shared/services/environment-loader.service';
import { AuthService } from './core/auth/auth.service';
import { AuthGuardWithForcedLogin } from './core/auth/auth-guard.service';
import { authModuleConfig } from './core/auth/auth-module-config';
import { createAuthConfig } from './core/auth/auth-config';
import { ErrorHandlerService } from './shared/services/error-handler.service';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withJsonpSupport } from '@angular/common/http';
import { AuthInterceptor } from './shared/services/http.interceptors';
import { provideAnimations } from '@angular/platform-browser/animations';
import { registerLocaleData } from '@angular/common';
import localeDA from '@angular/common/locales/da';
import { provideRouter } from '@angular/router';
import routes from './app.routes';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import 'moment/locale/da.js';
import { polyfillCountryFlagEmojis } from 'country-flag-emoji-polyfill';
import { FirebaseOptions, initializeApp } from '@firebase/app';
import { getFirestore } from '@firebase/firestore';
import { MatDatepickerIntl } from '@angular/material/datepicker';
import { getDanishMatDatepickerIntl } from './shared/logic/datepicker-intl';
import { MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/slide-toggle';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { appErrorCodes } from './shared/const/error-codes.const';
import { APP_ERROR_CODES, APP_LOG_SERVICE, ErrorMessageService, SnackbarService } from 'ok-angular-shared/components';
import { FIRESTORE_INSTANCE } from './shared/services/firestore-instance.token';

registerLocaleData(localeDA);
polyfillCountryFlagEmojis();

const globalRippleConfig: RippleGlobalOptions = {
  disabled: true,
  animation: {
    enterDuration: 0,
    exitDuration: 0,
  },
};
const windowLocal = (window as { [key: string]: any })['env'];

const firebaseConfig: FirebaseOptions = {
  apiKey: windowLocal['firebaseConfig_apiKey'],
  projectId: windowLocal['firebaseConfig_projectId'],
  appId: windowLocal['firebaseConfig_appId'],
};
const firebaseApp = initializeApp(firebaseConfig);

export function storageFactory(): OAuthStorage {
  return localStorage;
}

export function environmentProviderFactory(provider: EnvironmentLoaderService, authService: AuthService) {
  return () =>
    provider.load().then(async () => {
      const config = createAuthConfig();
      await authService.runInitialLoginSequence(config);
    });
}

export const MY_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

export const AppConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      BrowserModule,
      FormsModule,
      MatMenuModule,
      MatIconModule,
      ReactiveFormsModule,
      OAuthModule.forRoot(),
      InputMaskModule,
      GoogleMapsModule,
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: !isDevMode(),
        // Register the ServiceWorker as soon as the application is stable
        // or after 30 seconds (whichever comes first).
        registrationStrategy: 'registerWhenStable:30000',
      })
    ),
    AppState,
    CustomIconService,
    provideAppInitializer(() => inject(CustomIconService).init()),
    DatadogService,
    LocalStorageDataService,
    SnackbarService,
    ErrorMessageService,
    { provide: APP_ERROR_CODES, useValue: appErrorCodes },
    { provide: APP_LOG_SERVICE, useClass: DatadogService },
    { provide: MatPaginatorIntl, useValue: getPaginatorIntl() },
    { provide: MatDatepickerIntl, useValue: getDanishMatDatepickerIntl() },
    { provide: LOCALE_ID, useValue: 'da' },
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { disableClose: true } as MatDialogConfig },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 3000 } as MatSnackBarConfig },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline', subscriptSizing: 'dynamic' } as MatFormFieldDefaultOptions,
    },
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig },
    { provide: MAT_SELECT_CONFIG, useValue: { overlayPanelClass: 'overlay-pane-expandable', panelWidth: '' } as MatSelectConfig },
    {
      provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
      useValue: { overlayPanelClass: 'overlay-pane-expandable', panelWidth: '' } as MatAutocompleteDefaultOptions,
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: { touchGestures: 'auto', touchLongPressShowDelay: 0, touchendHideDelay: 3500 } as MatTooltipDefaultOptions,
    },
    { provide: MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS, useValue: { hideIcon: 'true' } },
    { provide: ErrorStateMatcher, useClass: InstantErrorStateMatcher },
    provideAppInitializer(() => {
      const initializerFn = environmentProviderFactory(inject(EnvironmentLoaderService), inject(AuthService));
      return initializerFn();
    }),
    AuthService,
    AuthGuardWithForcedLogin,
    { provide: AuthConfig, useFactory: createAuthConfig },
    { provide: OAuthModuleConfig, useValue: authModuleConfig },
    { provide: OAuthStorage, useFactory: storageFactory },
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
    provideAnimations(),
    provideRouter(routes),
    provideMomentDateAdapter(MY_FORMATS),
    {
      provide: FIRESTORE_INSTANCE,
      useValue: getFirestore(firebaseApp),
    },
  ],
};
